import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import * as Sentry from "@sentry/react";
import { useHistory, useLocation } from "react-router-dom";
import { useStickyTourStore } from "../../stores/stickyTourStore";
import { useStoryBibleStore } from "../../stores/storyBibleStore";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { useProjectStore } from "../../stores/projectStore";
import { useCommentStore } from "../../stores/commentStore";
import { AnchorIds, TourIds, WriteRewriteTourVariations, StoryBibleTourVariations, PluginsQuickstartTourVariations, FeedbackTourVariations, TourSignal } from "./TourTypes";
import StickyNote from "./StickyNote";
import { getWriteRewriteTour } from "./Tours/WriteRewrite/WriteRewriteTour";
import { FIND_TOUR_AGAIN_TOUR } from "./Tours/FindTourAgain";
import { CHAT_ONBOARDING_TOUR } from "./Tours/ChatOnboardingTour";
import { CHAT_ONBOARDING_COHORT_KEY } from "../../../../src/shared/constants";
import { getStoryBibleTour } from "./Tours/StoryBibleTour";
import { getPluginsQuickstartTour } from "./Tours/PluginsQuickstartTour";
import { getFeedbackTour } from "./Tours/FeedbackTour/FeedbackTour";
import { SERIES_FOLDER_INTRO_TOUR } from "./Tours/SeriesFolderIntroTour";
import { PARAGRAPH_SPACING_TOUR } from "./Tours/ParagraphSpacingTour";
import { completeStoryBibleTourExperiment } from "./TourContent";
import StickyTourDebugMenu from "./StickyTourDebugMenu";
import { sendAppEvent } from "../../Utils";
import Auth from "../../Auth";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { MIN_WIDTH_WHEN_SHOWING_COMMENTS } from "../../../../src/shared/constants";
import { shouldStartGeneratedProjectStoryBibleIntro } from "../AppHome/newProjectFlow";
import { useExitTour } from "./useExitTour";

// Shorter output length for tour: ~50 words (value 0 from WORMHOLE_OUTPUT_LENGTHS)
const TOUR_OUTPUT_LENGTH = 0;

// Toggle this to enable/disable the debug menu for sticky tours
const DEBUG_STICKY_TOURS = false;

interface UserData {
  update: (data: any) => Promise<void>;
  data?: {
    onboarding?: {
      storyBible?: {
        farthestStepId?: string;
        hasGenerated?: string[];
      };
      newProjectFlow?: {
        storyBibleIntroShown?: boolean;
      };
    };
  };
}

type AnchorPosition = {
  anchorId: string;
  top: number;
  left: number;
};

interface StickyNoteManagerProps {
  needsOnboardingTour: boolean;
  isLegacyProjectFlowTreatment: boolean | undefined;
  userData: UserData;
  onEndOnboardingTour: () => Promise<void> | void;
  userMetadata: any;
}

export default function StickyNoteManager({ needsOnboardingTour, isLegacyProjectFlowTreatment, userData, onEndOnboardingTour, userMetadata }: StickyNoteManagerProps) {
  const phase = useStickyTourStore((state) => state.phase);
  const setSteps = useStickyTourStore((state) => state.setSteps);
  const anchors = useStickyTourStore((state) => state.anchors);
  const [anchorPositions, setAnchorPositions] = useState<Record<string, AnchorPosition>>({});
  const steps = useStickyTourStore((state) => state.steps);
  const currentStepIndex = useStickyTourStore((state) => state.currentStepIndex);
  const activeStep = steps[currentStepIndex];
  const activeStepId = activeStep?.id;
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const startTour = useStickyTourStore((state) => state.startTour);
  const activeTourId = useStickyTourStore((state) => state.activeTourId);
  const activeTourVariation = useStickyTourStore((state) => state.activeTourVariation);
  const exitTour = useExitTour();
  const setRightBarCollapsed = useProjectStore((state) => state.setRightBarCollapsed);
  const emitSudoLayoutEvent = useSudoLayoutEventEmitter();
  const [showDebugMenu, setShowDebugMenu] = useState(false);
  const subscription = useSudowriteStore((state) => state.subscription); // pragma: allowlist secret
  const newProjectFlow = subscription?.hasAccessToNewProjectFlow;
  const history = useHistory();
  const location = useLocation<{ showGeneratedProjectStoryBibleIntro?: boolean }>();
  const hasStartedGeneratedProjectIntro = useRef(false);
  const legacyOnboardingCleanupInProgress = useRef(false);
  const hasCompletedChatOnboarding = useRef(false);

  // Expose console command for admins to toggle debug menu
  useEffect(() => {
    (window as any).stickyTourDebug = {
      show: () => {
        setShowDebugMenu(true);
        console.log("🟨 Sticky Tour Debug Menu enabled");
      },
      hide: () => {
        setShowDebugMenu(false);
        console.log("🟨 Sticky Tour Debug Menu disabled");
      },
      toggle: () => {
        setShowDebugMenu((prev) => {
          const next = !prev;
          console.log(`🟨 Sticky Tour Debug Menu ${next ? "enabled" : "disabled"}`);
          return next;
        });
      },
      // Starts the paragraph spacing tour immediately, skipping the
      // auto-start guards (seen flag, Medium spacing, doc contents). Run it
      // inside a project document so the toolbar/gear anchors exist. Note the
      // final step points at the Collapse Empty Paragraphs button, which only
      // renders when the doc has blank paragraphs (Enter-Enter one in first).
      startParagraphSpacing: () => {
        useStickyTourStore.getState().startTour(PARAGRAPH_SPACING_TOUR);
        console.info("🟨 Starting paragraph spacing tour (guards bypassed)");
      },
    };

    return () => {
      delete (window as any).stickyTourDebug;
    };
  }, []);

  // Runs after a tour ends
  useEffect(() => {
    if (phase !== "cleaning-up" || !userData) return;

    const runCleanup = async () => {
      // Read ALL values fresh from stores to avoid stale closures and dependency issues
      const { cleaningUpTourId, activeTourVariation: tourVariation, savedSettings, cleanupComplete, clearSavedSettings } = useStickyTourStore.getState();
      const { editorOptions, setEditorOptions } = useSudowriteStore.getState();

      if (!cleaningUpTourId) {
        cleanupComplete();
        return;
      }

      // ----- WriteRewrite Tour Cleanup -----
      if (cleaningUpTourId === TourIds.WRITE_REWRITE) {
        if (savedSettings.wormholeOutputLength !== undefined) {
          setEditorOptions({
            ...editorOptions,
            wormholeOutputLength: savedSettings.wormholeOutputLength,
          });
          // We need to persist to firebase to avoid it getting set back by race conditions
          userData.update({ wormholeOutputLength: savedSettings.wormholeOutputLength });
          clearSavedSettings();
        }

        if (tourVariation !== WriteRewriteTourVariations.FROM_HELP_MENU) {
          onEndOnboardingTour();
        }
      }

      // ----- Story Bible Tour Cleanup -----
      if (
        cleaningUpTourId === TourIds.STORY_BIBLE &&
        (tourVariation === StoryBibleTourVariations.NEW_PROJECT ||
          tourVariation === StoryBibleTourVariations.NEW_PROJECT_FLOW)
      ) {
        await userData.update({ "onboarding.storyBible.farthestStepId": "completed" });
        await completeStoryBibleTourExperiment();
      }

      // ----- Plugins Quickstart Tour Cleanup -----
      if (cleaningUpTourId === TourIds.PLUGINS_QUICKSTART) {
        if (tourVariation !== PluginsQuickstartTourVariations.FROM_HELP_MENU) {
          onEndOnboardingTour();
        }
      }

      // ----- Feedback / Chat Onboarding Tour Cleanup -----
      if (cleaningUpTourId === TourIds.FEEDBACK || cleaningUpTourId === TourIds.CHAT_ONBOARDING) {
        await onEndOnboardingTour();
        if (cleaningUpTourId === TourIds.CHAT_ONBOARDING) {
          // The parent completion flag may not have rendered before cleanup returns to idle.
          hasCompletedChatOnboarding.current = true;
        }
      }

      // ----- Paragraph Spacing Tour Cleanup -----
      if (cleaningUpTourId === TourIds.PARAGRAPH_SPACING) {
        // Mark seen on completion or early exit so it never auto-starts again
        userData.update({ hasSeenParagraphSpacingOnboarding: true });
      }

      // ----- Find Tour Again Cleanup -----
      if (cleaningUpTourId === TourIds.FIND_TOUR_AGAIN) {
        // No cleanup needed
      }

      cleanupComplete();
    };

    runCleanup();
  }, [phase, onEndOnboardingTour, userData]);

  // Runs before a new tour starts
  useEffect(() => {
    if (phase !== "setting-up" || !userData) return;

    const runSetup = async () => {
      // Read ALL values fresh from stores to avoid stale closures and dependency issues
      const { pendingTour, savedSettings, setSavedSettings, setupComplete } = useStickyTourStore.getState();
      const { editorOptions, setEditorOptions } = useSudowriteStore.getState();
      const { setCollapsedSections, setProseGeneratorActive } = useStoryBibleStore.getState();

      if (!pendingTour) {
        setupComplete();
        return;
      }
      const tourId = pendingTour.tour.id;

      // ----- WriteRewrite Tour Setup -----
      if (tourId === TourIds.WRITE_REWRITE) {
        setProseGeneratorActive(false); // Close Draft panel

        // Save and shorten wormhole output length
        if (savedSettings.wormholeOutputLength === undefined) {
          setSavedSettings({ wormholeOutputLength: editorOptions.wormholeOutputLength });
        }
        setEditorOptions({ ...editorOptions, wormholeOutputLength: TOUR_OUTPUT_LENGTH });
        // We need to persist to firebase to avoid it getting set back by race conditions
        userData.update({ wormholeOutputLength: TOUR_OUTPUT_LENGTH });
      }

      // ----- Story Bible Tour Setup -----
      if (tourId === TourIds.STORY_BIBLE) {
        // Uncollapse all sections
        setCollapsedSections({
          seed: false,
          genre: false,
          style: false,
          synopsis: false,
          characters: false,
          worldbuilding: false,
          outline: false,
        });
      }

      // ----- Plugins Quickstart Tour Setup -----
      if (tourId === TourIds.PLUGINS_QUICKSTART) {
        // No setup needed
      }

      // ----- Feedback Tour Setup -----
      if (tourId === TourIds.FEEDBACK) {
        setProseGeneratorActive(false); // Close Draft panel
      }

      // ----- Find Tour Again Setup -----
      if (tourId === TourIds.FIND_TOUR_AGAIN) {
        // No setup needed
      }

      // ----- Chat Onboarding Setup -----
      // The sticky anchors to the expanded right bar and points at chat, so
      // make sure the bar is open (the store applies before the project
      // mounts; the Chat tab is already the default for a new project).
      if (tourId === TourIds.CHAT_ONBOARDING) {
        useProjectStore.getState().setRightBarCollapsed(false);
      }

      setupComplete();
    };

    runSetup();
  }, [phase, userData]);

  // Runs when a tour is active, but we've refreshed the page or user has left and come back
  useEffect(() => {
    if (phase !== "active") return;
    if (process.env.NODE_ENV !== "development" && steps.length > 0) return; // development guard allows hot reloading to pick up changes to stickies
    if (!activeTourId) return;

    let tour = null;
    if (activeTourId === TourIds.WRITE_REWRITE && activeTourVariation) {
      tour = getWriteRewriteTour(activeTourVariation as WriteRewriteTourVariations);
    } else if (activeTourId === TourIds.FIND_TOUR_AGAIN) {
      tour = FIND_TOUR_AGAIN_TOUR;
    } else if (activeTourId === TourIds.STORY_BIBLE && activeTourVariation) {
      tour = getStoryBibleTour(activeTourVariation as StoryBibleTourVariations);
    } else if (activeTourId === TourIds.PLUGINS_QUICKSTART && activeTourVariation) {
      tour = getPluginsQuickstartTour(activeTourVariation as PluginsQuickstartTourVariations);
    } else if (activeTourId === TourIds.FEEDBACK) {
      tour = getFeedbackTour((activeTourVariation as FeedbackTourVariations) ?? FeedbackTourVariations.NOT_IMPORTED);
    } else if (activeTourId === TourIds.CHAT_ONBOARDING) {
      tour = CHAT_ONBOARDING_TOUR;
    } else if (activeTourId === TourIds.SERIES_FOLDER_INTRO) {
      tour = SERIES_FOLDER_INTRO_TOUR;
    } else if (activeTourId === TourIds.PARAGRAPH_SPACING) {
      tour = PARAGRAPH_SPACING_TOUR;
    }

    if (tour) {
      setSteps(tour.steps);
    }
  }, [phase, activeTourId, activeTourVariation, steps.length, setSteps]);

  // ============================================================
  // TOUR INITIATION - Decide when to auto-start tours
  // ============================================================
  useEffect(() => {
    if (!isLegacyProjectFlowTreatment || !userData || legacyOnboardingCleanupInProgress.current) return;
    const hasHandledStoryBible = !!userData.data?.onboarding?.storyBible?.farthestStepId;
    if (!needsOnboardingTour && hasHandledStoryBible) return;

    // Retire the signup tours the old treatment intentionally skipped. Do not
    // emit tour-completion analytics or overwrite existing Story Bible progress.
    legacyOnboardingCleanupInProgress.current = true;
    const retireOnboarding = async () => {
      if (!hasHandledStoryBible) {
        await userData.update({ "onboarding.storyBible.farthestStepId": "completed" });
      }
      if (needsOnboardingTour) await onEndOnboardingTour();
    };
    retireOnboarding().catch(Sentry.captureException).finally(() => {
      legacyOnboardingCleanupInProgress.current = false;
    });
  }, [isLegacyProjectFlowTreatment, needsOnboardingTour, onEndOnboardingTour, userData, userData?.data?.onboarding?.storyBible?.farthestStepId]);

  useEffect(() => {
    const hasShownIntro = Boolean(userData?.data?.onboarding?.newProjectFlow?.storyBibleIntroShown);

    if (
      !userData ||
      hasStartedGeneratedProjectIntro.current ||
      !shouldStartGeneratedProjectStoryBibleIntro({
        newProjectFlow,
        locationState: location.state,
        phase,
        hasShownIntro,
      })
    ) {
      return;
    }

    const nextLocationState = { ...location.state };
    delete nextLocationState.showGeneratedProjectStoryBibleIntro;
    history.replace({ ...location, state: nextLocationState });
    hasStartedGeneratedProjectIntro.current = true;
    void userData.update({ "onboarding.newProjectFlow.storyBibleIntroShown": true });
    startTour(getStoryBibleTour(StoryBibleTourVariations.NEW_PROJECT_FLOW));
  }, [
    history,
    location,
    newProjectFlow,
    phase,
    startTour,
    userData,
    userData?.data?.onboarding?.newProjectFlow?.storyBibleIntroShown,
  ]);

  useEffect(() => {
    if (isLegacyProjectFlowTreatment !== false) return;
    if (!userMetadata) return;
    if (phase !== "idle") return;

    let isCancelled = false;

    if (needsOnboardingTour) {
      if (!userMetadata?.survey?.completedAt) return;

      // Resume unfinished Chat onboarding if its local tour state was lost.
      // ".experiments" is misleading/a leftover from ab test.
      // Any user who has a chat onboarding will have this key.
      if (userMetadata?.experiments?.[CHAT_ONBOARDING_COHORT_KEY]) {
        if (hasCompletedChatOnboarding.current) return;
        startTour(CHAT_ONBOARDING_TOUR);
        return;
      }

      const responses = userMetadata?.survey?.responses;
      const didImport = responses?.["import-step"] === "imported";
      const helpTypes = responses?.["help-type"];
      const hasEditing = Array.isArray(helpTypes) && helpTypes.includes("editing");
      const hasWriting = Array.isArray(helpTypes) && helpTypes.includes("writing");
      const shouldShowEditingTour = hasEditing;
      const braindump = responses?.braindump;
      const braindumpNotSure = responses?.["braindump-not-sure"];

      if (shouldShowEditingTour) {
        const startEditingTour = async () => {
          sendAppEvent("Onboarding - Tour Path Determined", {
            onboardingPath: didImport ? "feedback_import" : "feedback_no_import",
            hasEditing,
            hasWriting,
            didImport,
          });

          let feedbackVariation = didImport ? FeedbackTourVariations.IMPORTED : FeedbackTourVariations.NOT_IMPORTED;

          // Seed the empty-signup project's first doc with the first 500 words of
          // the user's import so the IMPORTED variation has prose to test on.
          // If no import is found server-side (e.g. import was deleted), fall
          // back to the NOT_IMPORTED variation so the paste step still works.
          if (feedbackVariation === FeedbackTourVariations.IMPORTED) {
            try {
              const result = await Auth.fetchWithIdToken("/api/user-metadata/seed-feedback-tour-doc", {});
              if (!result?.prefilled) {
                feedbackVariation = FeedbackTourVariations.NOT_IMPORTED;
              }
            } catch (err) {
              feedbackVariation = FeedbackTourVariations.NOT_IMPORTED;
            }
          }

          if (isCancelled) return;
          startTour(getFeedbackTour(feedbackVariation));
        };

        startEditingTour();
      } else {
        const onboardingPath = braindump ? "braindump_provided" : braindumpNotSure ? "braindump_not_sure" : "imported";
        sendAppEvent("Onboarding - Tour Path Determined", {
          onboardingPath,
          hasBraindump: Boolean(braindump),
          hasBraindumpNotSure: Boolean(braindumpNotSure),
        });

        if (braindump) {
          startTour(getWriteRewriteTour(WriteRewriteTourVariations.ONBOARDING_NEW_BOOK));
        } else if (braindumpNotSure) {
          startTour(getWriteRewriteTour(WriteRewriteTourVariations.BRAINDUMP_NOT_SURE));
        } else {
          startTour(getWriteRewriteTour(WriteRewriteTourVariations.ONBOARDING_IMPORTED_PROJECT));
        }
      }
    }

    return () => {
      isCancelled = true;
    };
  }, [isLegacyProjectFlowTreatment, needsOnboardingTour, userMetadata, phase, startTour]);

  // Start Story Bible after the initial onboarding tour (write-rewrite, feedback, or chat) ends.
  useEffect(() => {
    if (isLegacyProjectFlowTreatment !== false) return;
    if (phase !== "idle") return;
    if (needsOnboardingTour) return;

    const hasCompletedStoryBibleOnboarding = !!userData?.data?.onboarding?.storyBible?.farthestStepId;

    if (!hasCompletedStoryBibleOnboarding) {
      startTour(getStoryBibleTour(StoryBibleTourVariations.NEW_PROJECT));
    }
  }, [isLegacyProjectFlowTreatment, needsOnboardingTour, userData?.data?.onboarding?.storyBible?.farthestStepId, phase, startTour]);

  // ============================================================
  // RUNTIME EFFECTS
  // ============================================================

  // Generated projects open directly into Story Bible. Bring Braindump and
  // its intro sticky into view once the anchor has mounted.
  useEffect(() => {
    if (
      activeTourId !== TourIds.STORY_BIBLE ||
      activeTourVariation !== StoryBibleTourVariations.NEW_PROJECT_FLOW ||
      activeStepId !== "1"
    ) {
      return;
    }

    const braindumpAnchor = anchors[AnchorIds.STORY_BIBLE_SEED]?.current;
    braindumpAnchor?.parentElement?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }, [activeStepId, activeTourId, activeTourVariation, anchors]);

  // This captures global click to move tour along, but also allows click to pass through.
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest("[data-sticky-tour-exit]")) {
        return;
      }
      sendFlowSignal(TourSignal.GLOBAL_CLICK);
    };

    if (activeStep?.listeningFor?.includes(TourSignal.GLOBAL_CLICK)) {
      document.addEventListener("click", handleGlobalClick, { capture: true });
      return () => {
        document.removeEventListener("click", handleGlobalClick, { capture: true });
      };
    }
  }, [activeStep, sendFlowSignal]);

  const stickyExitHandler =
    activeTourId && activeTourId !== TourIds.FIND_TOUR_AGAIN && activeTourId !== TourIds.CHAT_ONBOARDING
      ? exitTour
      : undefined;

  // Ensure right bar is open, chat is focused, and the feedback comments
  // sidebar is visible for the Feedback tour wrap-up step. If both bars open
  // would squeeze the center column below the comments-shown minimum, collapse
  // the left bar first so the user can actually read their prose and the
  // comments side-by-side.
  useEffect(() => {
    if (activeTourId !== TourIds.FEEDBACK || activeStepId !== "7") return;

    const { columnWidths, setLeftBarCollapsed: collapseLeft, setColumnWidths } = useProjectStore.getState();
    const rightWidthAfterOpen = Math.max(columnWidths.right.saved || columnWidths.right.openMin, columnWidths.right.openMin);
    const projectedCenterWidth = window.innerWidth - columnWidths.left.current - rightWidthAfterOpen;

    if (projectedCenterWidth < MIN_WIDTH_WHEN_SHOWING_COMMENTS && !columnWidths.left.collapsed) {
      collapseLeft(true);
    }

    setRightBarCollapsed(false);

    // Force-show the comments sidebar. Setting state directly (instead of
    // calling toggleComments) avoids that hook's side effect of collapsing
    // whichever bar is open when the center column is tight — we just opened
    // the right bar and don't want it bounced shut again.
    const { setShowComments, showComments } = useCommentStore.getState();
    if (!showComments) {
      setShowComments(true);
      setColumnWidths((draft: { center: { min: number } }) => {
        draft.center.min = MIN_WIDTH_WHEN_SHOWING_COMMENTS;
      });
    }

    emitSudoLayoutEvent("focus_chat", null);
  }, [activeTourId, activeStepId, setRightBarCollapsed, emitSudoLayoutEvent]);

  // Position tracking for sticky notes
  useEffect(() => {
    const stickies = activeStep?.stickies || [];
    const activeAnchorIds = stickies.map((sticky) => sticky.anchorId).filter((id) => id !== undefined) as string[];

    // Track last known positions to avoid unnecessary state updates
    let lastPositionsJson = "";

    const updatePositions = () => {
      const newPositions: Record<string, AnchorPosition> = {};

      activeAnchorIds.forEach((anchorId) => {
        const anchor = anchors[anchorId];
        if (anchor && anchor.current) {
          const rect = anchor.current.getBoundingClientRect();
          let top = rect.top + window.scrollY;
          let left = rect.left + window.scrollX;

          // If the anchor is in a different document (e.g. iframe), add the iframe's offset
          if (anchor.current.ownerDocument !== document) {
            const frameElement = anchor.current.ownerDocument.defaultView?.frameElement;
            if (frameElement) {
              const frameRect = frameElement.getBoundingClientRect();
              top += frameRect.top;
              left += frameRect.left;
            }
          }

          newPositions[anchorId] = {
            anchorId,
            top,
            left,
          };
        }
      });

      // Only update state if positions actually changed
      const newPositionsJson = JSON.stringify(newPositions);
      if (newPositionsJson !== lastPositionsJson) {
        lastPositionsJson = newPositionsJson;
        setAnchorPositions(newPositions);
      }
    };

    // Listener/rAF paths flush synchronously so notes track their anchors
    // within the same frame; under React 18 the render would otherwise land
    // after the frame paints. The initial updatePositions() call below must
    // stay async (flushSync warns inside React's passive-effect flush).
    const updatePositionsSync = () => flushSync(updatePositions);

    updatePositions();
    const windowsToListen = new Set<Window>();
    windowsToListen.add(window);

    activeAnchorIds.forEach((anchorId) => {
      const anchor = anchors[anchorId];
      if (anchor?.current?.ownerDocument?.defaultView) {
        windowsToListen.add(anchor.current.ownerDocument.defaultView);
      }
    });

    windowsToListen.forEach((win) => {
      win.addEventListener("scroll", updatePositionsSync, true);
      win.addEventListener("resize", updatePositionsSync);
    });

    // Continuously update positions to handle DOM reordering (e.g. project list updates)
    let rafId: number;
    const pollPositions = () => {
      updatePositionsSync();
      rafId = requestAnimationFrame(pollPositions);
    };
    rafId = requestAnimationFrame(pollPositions);

    return () => {
      windowsToListen.forEach((win) => {
        win.removeEventListener("scroll", updatePositionsSync, true);
        win.removeEventListener("resize", updatePositionsSync);
      });
      cancelAnimationFrame(rafId);
    };
  }, [anchors, activeStep]);

  return (
    <>
      {(showDebugMenu || DEBUG_STICKY_TOURS) && <StickyTourDebugMenu userData={userData} />}
      {activeStep?.stickies?.map((sticky, index) => {
        if (sticky.renderIfAnchorMissing && anchors[sticky.renderIfAnchorMissing]?.current) {
          return null;
        }

        // Handle fixed position stickies
        if (sticky.fixedPosition) {
          return (
            <StickyNote
              key={`fixed-${sticky.fixedPosition}-${index}`}
              content={sticky.content}
              offsetX={sticky.offsetX}
              offsetY={sticky.offsetY}
              rotation={sticky.rotation}
              arrowPosition={sticky.arrowPosition}
              isFixed={true}
              onExit={stickyExitHandler}
            />
          );
        }

        // Handle anchor-based stickies
        if (!sticky.anchorId) return null;
        const position = anchorPositions[sticky.anchorId];

        if (!position) return null;

        return (
          <StickyNote
            key={`${sticky.anchorId}-${index}`}
            content={sticky.content}
            offsetX={position.left + sticky.offsetX}
            offsetY={position.top + sticky.offsetY}
            rotation={sticky.rotation}
            arrowPosition={sticky.arrowPosition}
            onExit={stickyExitHandler}
          />
        );
      })}
    </>
  );
}
